/**
 * Marker-strip DOM creation, render, drag, wheel-zoom.
 *
 * Strip layout (from bottom): track (4px line) + ticks (8px) + regions (full-height).
 * Cursor is rendered as a 2px vertical line, hovertip floats above on mouseover.
 *
 * The strip is wired up by ZapPlayer once and renders on demand via render().
 */
import { fmtTime, fmtTimeShort, escAttr, esc } from '../core/utils.js';
import { visibleDur, leftBoundT, timeFromX, applyWheelZoom, applyWheelPan } from '../core/timeline.js';
import { srvUpdate, srvDelete } from '../core/regions.js';

const MODE_ICONS = {
  skip:           '⏭',
  include:        '▶',
  repeat:         '🔁',
  reverse:        '◀',
  'fwd-back-fwd': '▶◀▶',
};

function renderTicks(ticksEl, leftT, vd, zoomLevel, fps) {
  let stepSec;
  if (zoomLevel < 4) { ticksEl.innerHTML = ''; return; }
  else if (zoomLevel < 20)  stepSec = 10;
  else if (zoomLevel < 50)  stepSec = 1;
  else if (zoomLevel < 100) stepSec = 0.5;
  else                      stepSec = 1 / (fps || 24);
  let html = '';
  const firstTick = Math.ceil(leftT / stepSec) * stepSec;
  for (let t = firstTick; t < leftT + vd + stepSec; t += stepSec) {
    const x = ((t - leftT) / vd) * 100;
    if (x < 0 || x > 100) continue;
    const cls = 'zap-pe-tick ' + (stepSec >= 1 ? 'zap-pe-tick--major' : 'zap-pe-tick--minor');
    html += `<div class="${cls}" style="left:${x.toFixed(3)}%;"></div>`;
    if (stepSec >= 10 || (stepSec >= 1 && Math.round(t) % 5 === 0)) {
      html += `<div class="zap-pe-tick-label" style="left:${x.toFixed(3)}%;">${fmtTimeShort(t)}</div>`;
    }
  }
  ticksEl.innerHTML = html;
}

export function createStrip() {
  const strip = document.createElement('div');
  strip.className = 'zap-pe-strip';
  strip.innerHTML = '<div class="zap-pe-track"></div>'
                  + '<div class="zap-pe-ticks"></div>'
                  + '<div class="zap-pe-regions"></div>'
                  + '<div class="zap-pe-cursor"></div>'
                  + '<div class="zap-pe-hovertip" hidden></div>';
  return strip;
}

/**
 * Render the current state into the strip. Pure render — reads from `state`,
 * writes only to the DOM nodes inside `strip`.
 *
 * @param {Object} args
 * @param {HTMLVideoElement} args.video
 * @param {HTMLElement} args.strip
 * @param {Array<Object>} args.regions
 * @param {Object} args.timeline    { duration, zoomLevel, panOffsetT }
 * @param {string} args.loopState   'none' | 'markerSet' | 'looping'
 * @param {number} args.loopStart
 * @param {number} args.loopEnd
 * @param {Object|null} args.activeRegion
 * @param {number} args.fps
 */
export function renderStripContent(args) {
  const { video, strip, regions, timeline, loopState, loopStart, loopEnd, activeRegion, fps } = args;
  const regionsEl = strip.querySelector('.zap-pe-regions');
  const ticksEl   = strip.querySelector('.zap-pe-ticks');
  const cursor    = strip.querySelector('.zap-pe-cursor');

  const dur = video.duration || 0;
  if (!dur) { regionsEl.innerHTML = ''; ticksEl.innerHTML = ''; return; }
  const vd = visibleDur(timeline);
  const leftT = leftBoundT(timeline);

  let html = '';
  regions.forEach((r, i) => {
    if (r.end < leftT || r.start > leftT + vd) return;
    const l = ((r.start - leftT) / vd) * 100;
    const w = ((r.end - r.start) / vd) * 100;
    const lc = Math.max(0, l), rc = Math.min(100, l + w);
    const wPct = Math.max(0.5, rc - lc);
    const modeKey = r.enabled ? (r.mode || 'skip') : 'off';
    const cls = 'zap-pe-region zap-pe-region--' + modeKey
              + (activeRegion === r ? ' is-active' : '');
    const icon = MODE_ICONS[r.mode || 'skip'] || '⏭';
    const repTxt = (r.mode === 'repeat' || r.mode === 'fwd-back-fwd') && r.repeat_count
      ? r.repeat_count + '×' : '';
    const speedTxt = r.speed && Math.abs(r.speed - 1) > 0.001 ? ' ' + r.speed + '×' : '';
    html += `<div class="${cls}" data-idx="${i}" style="left:${lc.toFixed(3)}%;width:${wPct.toFixed(3)}%;"
                   title="${escAttr((r.label || 'Region '+(i+1)) + ' · ' + fmtTime(r.start) + '–' + fmtTime(r.end) + ' · ' + (r.mode || 'skip'))}">
               <span class="zap-pe-handle zap-pe-handle--start" data-side="start"></span>
               <span class="zap-pe-mode-icon">${icon}${repTxt}${speedTxt}</span>
               <span class="zap-pe-region-label">${esc(r.label || 'Region '+(i+1))}</span>
               <button class="zap-pe-region-toggle" data-act="toggle">${r.enabled?'✓':'○'}</button>
               <button class="zap-pe-region-del" data-act="del">×</button>
               <span class="zap-pe-handle zap-pe-handle--end" data-side="end"></span>
             </div>`;
  });

  if (loopState !== 'none') {
    const ls = loopStart;
    const le = loopState === 'looping' ? loopEnd : video.currentTime;
    const l = ((ls - leftT) / vd) * 100;
    const w = ((le - ls) / vd) * 100;
    if (le >= leftT && ls <= leftT + vd) {
      const lc = Math.max(0, l), rc = Math.min(100, l + w);
      const wPct = Math.max(0.5, rc - lc);
      html += `<div class="zap-pe-region zap-pe-region--include" style="left:${lc.toFixed(3)}%;width:${wPct.toFixed(3)}%;border-color:#1ED760;background:rgba(30,215,96,.55);" data-loop="1">
                 <span class="zap-pe-handle zap-pe-handle--start" data-side="loopStart"></span>
                 <span class="zap-pe-region-label">A→B</span>
                 <span class="zap-pe-handle zap-pe-handle--end" data-side="loopEnd"></span>
               </div>`;
    }
  }
  regionsEl.innerHTML = html;
  renderTicks(ticksEl, leftT, vd, timeline.zoomLevel || 1, fps);
  if (cursor) {
    const x = ((video.currentTime - leftT) / vd) * 100;
    cursor.style.left = Math.max(0, Math.min(100, x)) + '%';
  }
}

/**
 * Wire up drag/click/wheel/hover handlers. Uses callbacks so the host
 * (ZapPlayer) keeps state ownership.
 */
export function attachStripHandlers(opts) {
  const {
    strip, video, regions, timeline, loop, openPopover, writeCache,
    rawPath, renderStrip, updateBanner,
  } = opts;
  const hovertip = strip.querySelector('.zap-pe-hovertip');

  let drag = null;

  // Drag-Render-Throttle: pointermove feuert pro Frame (60-120/s); ohne das
  // baut renderStrip() das komplette Strip-DOM bei jedem Event neu → Jank.
  // Region-Daten werden weiter synchron pro Event mutiert; nur das visuelle
  // Re-Render koalesziert auf 1× pro animation-frame.
  let renderPending = false;
  const scheduleRender = () => {
    if (renderPending) return;
    renderPending = true;
    requestAnimationFrame(() => {
      renderPending = false;
      renderStrip();
      updateBanner();
    });
  };

  strip.addEventListener('pointerdown', (e) => {
    const handle = e.target.closest('.zap-pe-handle');
    if (!handle) return;
    const region = handle.closest('.zap-pe-region');
    drag = {
      idx:  region?.dataset.idx ? parseInt(region.dataset.idx) : null,
      side: handle.dataset.side,
    };
    strip.setPointerCapture(e.pointerId);
    e.preventDefault(); e.stopPropagation();
  });

  strip.addEventListener('pointermove', (e) => {
    const dur = video.duration || 0;
    if (dur && !drag) {
      const t = timeFromX(timeline, strip, e.clientX);
      opts.setHoverT(t);
      hovertip.hidden = false;
      hovertip.textContent = fmtTime(t);
      const rect = strip.getBoundingClientRect();
      hovertip.style.left = (e.clientX - rect.left) + 'px';
    }
    if (!drag) return;
    const t = timeFromX(timeline, strip, e.clientX);
    if (drag.side === 'loopStart')      loop.loopStart = Math.min(t, loop.loopEnd - 0.5);
    else if (drag.side === 'loopEnd')   loop.loopEnd   = Math.max(t, loop.loopStart + 0.5);
    else if (drag.idx !== null) {
      const r = regions[drag.idx];
      if (drag.side === 'start') r.start = Math.min(Math.max(0, t), r.end - 0.5);
      else                       r.end   = Math.max(t, r.start + 0.5);
    }
    scheduleRender();
  });

  strip.addEventListener('pointerup', (e) => {
    if (!drag) return;
    try { strip.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    if (drag.idx !== null) {
      writeCache();
      srvUpdate(rawPath, regions[drag.idx]);
    }
    drag = null;
    // Finaler Sync-Render: garantiert den End-Zustand sofort (statt erst beim
    // naechsten Frame), falls der letzte pointermove-Render noch pending war.
    renderStrip(); updateBanner();
  });

  strip.addEventListener('pointerleave', () => {
    hovertip.hidden = true;
    opts.setHoverT(null);
  });

  strip.addEventListener('click', (e) => {
    const region = e.target.closest('.zap-pe-region');
    if (!region) {
      const dur = video.duration || 0;
      if (!dur) return;
      try { video.currentTime = timeFromX(timeline, strip, e.clientX); } catch { /* noop */ }
      return;
    }
    if (region.dataset.loop) return;
    const idx = parseInt(region.dataset.idx);
    const act = e.target.closest('[data-act]')?.dataset.act;
    if (act === 'toggle') {
      regions[idx].enabled = !regions[idx].enabled;
      writeCache(); srvUpdate(rawPath, regions[idx]); renderStrip();
      return;
    }
    if (act === 'del') {
      const removed = regions[idx];
      regions.splice(idx, 1);
      writeCache(); renderStrip();
      if (removed?.id) srvDelete(removed.id);
      return;
    }
    if (e.target.closest('.zap-pe-handle')) return;
    openPopover(idx);
  });

  strip.addEventListener('wheel', (e) => {
    e.preventDefault();
    const dur = video.duration || 0;
    if (!dur) return;
    if (e.shiftKey) {
      applyWheelPan(timeline, e.deltaY);
    } else {
      applyWheelZoom(timeline, strip, e.clientX, e.deltaY);
    }
    renderStrip();
  }, { passive: false });
}
